import { useState } from 'react';
import { Paper } from '@mui/material';

import { LaunchpadProgressProps, LaunchpadItem, LaunchpadSubtask, TaskStatus } from './LaunchpadProgress.interface';
import LaunchpadItemComponent from './LaunchpadItem';
import { getNextLaunchpadTask, isLaunchpadItemBlocked } from './launchpadItemState';

function LaunchpadProgress({
    items,
    onItemClick,
    onSubtaskClick,
    className,
    itemTitleVariant = 'subtitle1',
    itemDescriptionVariant = 'body1',
    fullWidth = false,
    inlineExpandToggle = false,
}: LaunchpadProgressProps) {
    const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
        const currentTask = items.find((item) => !item.allSubtasksCompleted);
        return currentTask ? new Set([currentTask.id]) : new Set();
    });

    const toggleExpanded = (itemId: string) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    const isItemBlocked = (item: LaunchpadItem): boolean => isLaunchpadItemBlocked(item, items);

    const getLatestInProgressSubtask = (item: LaunchpadItem): LaunchpadSubtask | null => {
        const inProgressSubtasks = item.subtasks.filter((subtask) => subtask.status === TaskStatus.IN_PROGRESS);
        return inProgressSubtasks.length > 0 ? inProgressSubtasks[0] : null;
    };

    const handleItemClick = (item: LaunchpadItem) => {
        if (isItemBlocked(item)) {
            return;
        }

        if (item.subtasks.length > 0) {
            const latestSubtask = getLatestInProgressSubtask(item);
            if (latestSubtask && onSubtaskClick) {
                onSubtaskClick(latestSubtask);
            } else if (onItemClick) {
                onItemClick(item);
            }
        } else if (onItemClick) {
            onItemClick(item);
        }
    };

    return (
        <Paper
            className={className}
            variant="outlined"
            sx={{ py: 1, width: '100%', maxWidth: fullWidth ? 'none' : '900px', alignSelf: 'center' }}>
            {items.map((item, index) => (
                <LaunchpadItemComponent
                    key={item.id}
                    item={item}
                    index={index}
                    totalItems={items.length}
                    expandedItems={expandedItems}
                    onToggleExpanded={toggleExpanded}
                    onItemClick={handleItemClick}
                    onSubtaskClick={onSubtaskClick}
                    getMostRecentNotCompletedTask={getNextLaunchpadTask}
                    isItemBlocked={isItemBlocked}
                    titleVariant={itemTitleVariant}
                    descriptionVariant={itemDescriptionVariant}
                    inlineExpandToggle={inlineExpandToggle}
                />
            ))}
        </Paper>
    );
}

export default LaunchpadProgress;
